import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { MdFreeBreakfast, MdKeyboardArrowDown } from 'react-icons/md';

import { BookButton } from 'components/bookButton';

import { setSelectedRoom } from '../../features/roomsSlice';

import { RadioButtonRatesGroup } from 'components/radioButtonRatesGroup';
import {
    setSelectedFormRoom,
    setSelectedRateID,
    setSelectedRoomForm,
} from 'features/roomBookingFormSlice';
import {
    addCartItem,
    setAddingRate,
    setCartOpenState,
    setSmallCartOpenState,
    setTempRate,
} from 'features/cart/cartSlice';
import { openModal } from 'features/modalSlice';
import axios from 'axios';
import { toast } from 'react-toastify';
import { makeStatsRequestThrottled } from 'lib';
import { WIDGET_STATS_SETTINGS, apiConfig } from 'config';

const MobileRoom = ({ room = {}, index, isBestRate, property }) => {
    const dispatch = useDispatch();

    const Lang = useSelector((state) => state.miniBookingWidget?.data?.Lang);

    const roomRef = useRef(null);
    const { currencySymbol } = useSelector(
        (state) => state.miniBookingWidget?.data?.ourPrice
    );

    let defaultRate;

    const [selectedRate, setSelectedRate] = useState(() => {
        if (Array.isArray(room.rates) && room.rates.length > 0) {
            return room.rates[0];
        }
        return {};
    });

    if (Array.isArray(room.rates) && room.rates.length > 0) {
        defaultRate = room.rates[0];
    }

    const openRoomDetails = (roomIndex) => {
        dispatch(setSelectedRoom({ selectedRoomIndex: roomIndex }));
    };

    const isDirect = useSelector(
        (state) => state.miniBookingWidget?.data?.bookDirectly
    );
    const propCode = useSelector(
        (state) => state.miniBookingWidget?.data?.propCode
    );
    const propertyID = useSelector(
        (state) => state.miniBookingWidget?.data?.propertyID
    );
    const hasCart = useSelector(
        (state) => state.miniBookingWidget?.data?.hasCart
    );
    const cartItems = useSelector((state) => state.cart?.cartItems);
    const searchOptions = useSelector((state) => state.user);
    const cartID = useSelector(
        (state) => state.miniBookingWidget?.data?.cartID
    );
    let noRedirect = false;
    if (hasCart || isDirect) {
        noRedirect = true;
    }
    const isAddingRate = useSelector((state) => state.cart?.isAddingRate);

    const thisRoomCartItems = cartItems.filter(
        (item) => item.roomTypeID === room.roomTypeID
    );

    const openRoomBookingForm = async (roomIndex, rateID, room) => {
        if (hasCart) {
            if (isAddingRate) {
                return;
            }
            if (room) {
                const { name, type, rates, photos, roomTypeID } = room;
                const roomName = name;
                const roomType = type;
                const price = rates.find((rate) => rate.rateID === rateID)
                    .pricing?.price;
                const prePayPrice = rates.find((rate) => rate.rateID === rateID)
                    .pricing?.prePayPrice;
                const roomPhoto = photos?.[0]?.large;
                const rateName = rates.find(
                    (rate) => rate.rateID === rateID
                ).name;
                if (cartItems.some((item) => item.roomTypeID === roomTypeID)) {
                    if (cartItems.some((item) => item.rateID === rateID)) {
                        dispatch(setCartOpenState(true));
                    } else {
                        dispatch(
                            setTempRate({
                                tempRate: rateID,
                                roomTypeID: roomTypeID,
                            })
                        );
                        dispatch(
                            openModal({
                                modalId: 'AreYouSureModal',
                            })
                        );
                    }
                } else {
                    if (!cartItems.some((item) => item.rateID === rateID)) {
                        const cartItem = {
                            rateID: rateID,
                            hotelCode: propCode,
                            roomName,
                            roomType,
                            price,
                            roomPhoto,
                            roomTypeID,
                            quantity: searchOptions?.numberOfRooms,
                            adults: searchOptions?.adults,
                            children: searchOptions?.children,
                            checkin: searchOptions?.checkInDay.date,
                            checkout: searchOptions?.checkOutDay.date,
                            cartID,
                            rateName,
                            isCompleted: false,
                            totalPrice:
                                Number(price) *
                                Number(searchOptions?.numberOfRooms),
                            extras: [],
                            prePayPrice,
                        };
                        dispatch(setAddingRate(true));
                        const response = await addToCartBack(cartItem);
                        if (response?.status === 'ok') {
                            cartItem.ID = response.ID;
                            dispatch(setSmallCartOpenState(true));
                            dispatch(addCartItem(cartItem));
                        }
                        dispatch(setAddingRate(false));
                    } else {
                        dispatch(setCartOpenState(true));
                    }
                }
            }
        } else if (isDirect) {
            dispatch(setSelectedRoomForm({ selectedRoomFormIndex: roomIndex }));
            dispatch(setSelectedRateID({ selectedRateID: rateID }));
            dispatch(setSelectedFormRoom({ room: room }));
        } else {
            makeStatsRequestThrottled(
                WIDGET_STATS_SETTINGS.actions.booknow,
                propertyID
            );
        }
    };

    const addToCartBack = async (cartItem) => {
        try {
            const response = await axios.post(apiConfig?.addToCartBack, {
                ...cartItem,
            });

            if (response?.data.status.toLowerCase() === 'ok') {
                return response?.data;
            } else {
                toast.error(Lang?.somethingWentWrong, {
                    autoClose: true,
                    theme: 'colored',
                    PlacementType: 'top-left',
                });
                return 'error';
            }
        } catch (error) {
            toast.error(Lang?.networkError, {
                autoClose: true,
                theme: 'colored',
            });
            return 'error';
        }
    };

    useEffect(() => {
        if (thisRoomCartItems && thisRoomCartItems.length > 0) {
            const cartSelectedRateID = thisRoomCartItems[0].rateID;
            const cartSelectedRate = room.rates.find(
                (rate) => rate.rateID === cartSelectedRateID
            );
            setSelectedRate(cartSelectedRate);
        }
    }, [thisRoomCartItems]);

    const BestPriceText =
        property?.ourPrice?.text ||
        useSelector((state) => state.miniBookingWidget?.data?.ourPrice.text);

    if (!defaultRate) {
        return null;
    }

    return (
        <>
            <div className="ab-room-row" ref={roomRef}>
                <div className="ab-room-row-main ab-flex-column">
                    <div className="ab-bestprice-ribbon-multiproperty">
                        {BestPriceText}
                    </div>
                    <div
                        className="ab-room-image-container"
                        onClick={() => openRoomDetails(index)}
                    >
                        <div
                            className="ab-room-image"
                            style={{
                                backgroundImage: `url("${room?.url?.photoL}")`,
                            }}
                        ></div>
                        {/* {room?.photos?.length > 0 && (
                            <div className="ab-more-photos">
                                +{room?.photos?.length} {room.photoText}
                            </div>
                        )} */}
                    </div>
                    <div className="ab-room-middle-container">
                        <div className="ab-room-middle-upper">
                            <div className="ab-room-upper-head">
                                <div
                                    className="ab-room-mobile-title"
                                    onClick={() => openRoomDetails(index)}
                                >
                                    <div className="ab-room-upper-name">
                                        {room?.name}
                                    </div>
                                    {room?.roomAvail > 0 &&
                                        (defaultRate.pricing?.memberPrice ? (
                                            <div
                                                className="ab-price"
                                                tabIndex="0"
                                                role="button"
                                            >
                                                {
                                                    selectedRate?.pricing
                                                        .memberPrice
                                                }{' '}
                                                {currencySymbol}
                                                <span className="ab-original-price">
                                                    {
                                                        selectedRate?.pricing
                                                            ?.price
                                                    }{' '}
                                                    {currencySymbol}
                                                </span>
                                            </div>
                                        ) : (
                                            <div
                                                className="ab-price"
                                                tabIndex="0"
                                                role="button"
                                            >
                                                {selectedRate?.pricing?.price}{' '}
                                                {currencySymbol}
                                            </div>
                                        ))}
                                </div>

                                <div className="ab-prices-buttons">
                                    <div
                                        className="ab-prices-buttons-left"
                                        onClick={() => openRoomDetails(index)}
                                    >
                                        <div className="ab-room-upper-save ab-save-box">
                                            {property?.ourPrice
                                                ?.hasBreakfast ? (
                                                <>
                                                    {selectedRate?.hasBreakfast && (
                                                        <div className="ab-freebies">
                                                            <div className="ab-freebie">
                                                                <MdFreeBreakfast
                                                                    size={20}
                                                                />
                                                                <span className="ab-breakfast-text">
                                                                    {
                                                                        Lang.breakfastText
                                                                    }
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                        {selectedRate?.hasMinStay && (
                                            <span className="ab-pill warning">
                                                Min-Stay
                                            </span>
                                        )}
                                        <div className="ab-room-upper-occupancy">
                                            {room?.remainingText}
                                        </div>
                                        {selectedRate?.pricing?.savePrice > 0 &&
                                            room?.roomAvail > 0 && (
                                                <div className="ab-providerWrap">
                                                    <div className="ab-provider">
                                                        {
                                                            selectedRate
                                                                ?.pricing
                                                                .saveText
                                                        }{' '}
                                                        <span className="ab-discount">
                                                            {
                                                                selectedRate
                                                                    ?.pricing
                                                                    .savePrice
                                                            }{' '}
                                                            {currencySymbol}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                        {selectedRate?.cancelationPolicy &&
                                            room?.roomAvail > 0 && (
                                                <div className="ab-freebies">
                                                    <div className="ab-freebie">
                                                        {
                                                            selectedRate.cancelationPolicyType
                                                        }
                                                        {selectedRate?.freeCancelationText && (
                                                            <div className="ab-cancel-policy-text">
                                                                {
                                                                    selectedRate.freeCancelationText
                                                                }
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                    </div>

                                    {room?.roomAvail > 0 ? (
                                        <div className="ab-prices-buttons-right">
                                            <BookButton
                                                currencySymbol={currencySymbol}
                                                price={
                                                    selectedRate?.pricing?.price
                                                }
                                                text={
                                                    noRedirect
                                                        ? Lang?.select
                                                        : selectedRate
                                                              ?.availableRooms[0]
                                                              ?.BookNowText
                                                }
                                                url={
                                                    selectedRate
                                                        ?.availableRooms[0]
                                                        ?.bookNowUrl
                                                }
                                                variant="non-member"
                                                onClick={() =>
                                                    openRoomBookingForm(
                                                        index,
                                                        defaultRate?.rateID,
                                                        room
                                                    )
                                                }
                                                isDirect={isDirect}
                                                noRedirect={noRedirect}
                                                isLoading={isAddingRate}
                                            />
                                            <BookButton
                                                currencySymbol={currencySymbol}
                                                price={
                                                    selectedRate?.pricing
                                                        ?.memberPrice
                                                }
                                                text={
                                                    noRedirect
                                                        ? Lang?.select
                                                        : selectedRate
                                                              ?.availableRooms[0]
                                                              ?.memberBookNowText
                                                }
                                                url={
                                                    selectedRate
                                                        ?.availableRooms[0]
                                                        ?.memberBookNowUrl
                                                }
                                                variant="member"
                                                onClick={() =>
                                                    openRoomBookingForm(
                                                        index,
                                                        defaultRate?.rateID,
                                                        room
                                                    )
                                                }
                                                isDirect={isDirect}
                                                noRedirect={noRedirect}
                                                isLoading={isAddingRate}
                                            />
                                        </div>
                                    ) : (
                                        <span>{Lang.soldOut}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        {room?.rates?.length > 1 && (
                            <div className="ab-room-middle-footer">
                                <div className="ab-expand-options">
                                    <div
                                        className="ab-show-more"
                                        onClick={() => {
                                            if (roomRef.current) {
                                                roomRef.current.classList.add(
                                                    'expanded'
                                                );
                                            }
                                        }}
                                    >
                                        <span className="icon">
                                            <MdKeyboardArrowDown size={20} />
                                        </span>
                                        <span>{room.seeMoreOffers}</span>
                                        <span className="ab-moreRates-price">
                                            {room?.seeMoreOffersPrice}
                                        </span>
                                    </div>
                                    <div
                                        className="ab-show-less"
                                        onClick={() => {
                                            if (roomRef.current) {
                                                roomRef.current.classList.remove(
                                                    'expanded'
                                                );
                                            }
                                        }}
                                    >
                                        <span className="icon">
                                            <MdKeyboardArrowDown size={20} />
                                        </span>
                                        <span>{room.seeLessOffers}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {room?.rates?.length > 1 && (
                    <div className="ab-room-more-offers">
                        <RadioButtonRatesGroup
                            setSelectedRate={setSelectedRate}
                            rates={room.rates}
                            defaultRate={defaultRate}
                            currencySymbol={currencySymbol}
                            outerClass="ab-room-extra-offer-name"
                            wrapperClass="ab-room-offer-title"
                            rightLabelClass="ab-room-offer-notes"
                        />
                    </div>
                )}
            </div>
        </>
    );
};

MobileRoom.propTypes = {
    room: PropTypes.object,
    property: PropTypes.object,
    index: PropTypes.number,
    isBestRate: PropTypes.bool,
};

export default MobileRoom;
